import { Button, Input, Modal, Pagination, message } from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import addIcon from "../../images/add-icon.svg";
import searchIcon from "../../images/search_icon.png";
import { api } from "../../utils/api";

interface AppListItem {
  id: number;
  name: string;
  description: string;
  logoUrl: string;
  latestVersion: string | null;
}

const AdminAppsPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<AppListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = React.useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get("search") || "";

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get("/api/admin/apps", {
          params: { page, pageSize, search: searchTerm },
        });
        if (!alive) return;
        setItems(res.data.items);
        setTotal(res.data.total);
      } catch {
        setItems([]);
        setTotal(0);
      } finally {
        if (alive) setLoading(false);
      }
    })().catch(() => {
      setItems([]);
      setTotal(0);
    });
    return () => {
      alive = false;
    };
  }, [page, searchTerm]);

  const editApp = (id: number) => {
    navigate(`/admin/apps/${id}/edit`);
  };

  const updateVersion = (id: number) => {
    navigate(`/admin/apps/${id}/versions`);
  };

  const confirmDeleteApp = (app: AppListItem) => {
    Modal.confirm({
      title: "アプリを削除しますか？",
      content: `「${app.name}」を削除すると復元できません。よろしいですか？`,
      okText: "削除",
      okButtonProps: { danger: true },
      cancelText: "キャンセル",
      onOk: async () => {
        try {
          await api.delete(`/api/admin/apps/${app.id}`);
          message.success("アプリを削除しました。");
          // refresh
          const res = await api.get("/api/admin/apps", {
            params: { page, pageSize },
          });
          setItems(res.data.items);
          setTotal(res.data.total);
        } catch {
          message.error("削除に失敗しました。");
        }
      },
    });
  };

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  const onSearch = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (searchInput) {
        params.set("search", searchInput);
      } else {
        params.delete("search");
      }
      params.set("page", "1");
      return params;
    });
  };

  return (
    <>
      <h2 className="m-0 text-lg font-normal w-fit mb-6">アプリ管理</h2>
      <div className="flex w-full flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="flex gap-3">
            <Input
              maxLength={255}
              placeholder="アプリ検索"
              className="base-input h-11 w-[424px]"
              suffix={
                <img src={searchIcon} className="h-6" alt="search icon" />
              }
              onChange={(e) => {
                setSearchInput(e.target.value);
              }}
              value={searchInput}
            />
            <Button
              onClick={() => onSearch()}
              className="h-[46px] w-[52px] base-button tracking-[-2px]"
            >
              検索
            </Button>
          </div>
          <button
            onClick={() => navigate("/admin/apps/new")}
            className="base-button w-[128px] h-[46px] flex gap-[10px] items-center justify-center"
          >
            <img src={addIcon} alt="アプリ登録アイコン" className="h-6 w-6" />
            <span>アプリ登録</span>
          </button>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="w-full table-fixed border-collapse">
            <thead className="bg-gray-100">
              <tr className="h-12">
                <th className="font-normal w-32 border-b border-gray-200 px-3 py-2 text-left">
                  ロゴ
                </th>
                <th className="font-normal w-56 border-b border-gray-200 px-3 py-2 text-left">
                  名前
                </th>
                <th className="font-normal border-b border-gray-200 px-3 py-2 text-left">
                  説明
                </th>
                <th className="font-normal w-40 border-b border-gray-200 px-3 py-2 text-left">
                  最新バージョン
                </th>
                <th className="font-normal w-80 border-b border-gray-200 px-3 py-2 text-start">
                  アクション
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((app) => (
                <tr key={app.id} className="align-middle h-[68px]">
                  <td className="border-b border-gray-100 px-3 py-2">
                    <img
                      src={app.logoUrl}
                      alt={`${app.name} のロゴ`}
                      className="h-[41.98px] w-[41.98px] object-contain"
                    />
                  </td>
                  <td className="text-left border-b border-gray-100 px-3 py-2 font-normal">
                    {app.name}
                  </td>
                  <td className="text-left border-b border-gray-100 px-3 py-2 text-gray-600">
                    {app.description}
                  </td>
                  <td className="text-left border-b border-gray-100 px-3 py-2">
                    {app.latestVersion ?? "-"}
                  </td>
                  <td className="text-left border-b border-gray-100 px-3 py-2">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => editApp(app.id)}
                        className="base-button-outline w-[75px] h-[42px]"
                      >
                        編集
                      </button>
                      <button
                        onClick={() => updateVersion(app.id)}
                        className="base-button w-[122px] h-[42px]"
                      >
                        バージョン更新
                      </button>
                      <button
                        onClick={() => confirmDeleteApp(app)}
                        className="base-button-danger w-[52px]"
                      >
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-end gap-2 h-[46px] pr-6">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 mr-auto">
                全 {total}件
              </span>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                onChange={(p) => setPage(p)}
                showSizeChanger={false}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminAppsPage;
